import { motion } from "framer-motion";
import { Camera, Landmark, Compass, Route as RouteIcon, ArrowUpRight, CalendarDays } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { whatsappUrl } from "../../lib/leads";

const ONE_DAY = [
  { city: "Jodhpur", desc: "Mehrangarh Fort, Jaswant Thada & the Blue City lanes.", icon: Landmark },
  { city: "Jaipur", desc: "Amber Fort, Hawa Mahal & City Palace.", icon: Landmark },
  { city: "Jaisalmer", desc: "Golden Fort, Patwon Ki Haveli & Sam dunes.", icon: Compass },
  { city: "Udaipur", desc: "City Palace, Lake Pichola & Jag Mandir.", icon: Camera },
  { city: "Agra", desc: "Taj Mahal, Agra Fort & Mehtab Bagh.", icon: Landmark },
  { city: "Delhi", desc: "India Gate, Qutub Minar & Red Fort.", icon: Camera },
];

const PACKAGES = [
  { title: "Bishnoi Village Safari", sub: "Jodhpur cultural & wildlife safari", meta: "Half day", icon: Compass },
  { title: "Char Dham Yatra", sub: "Sacred four-shrine pilgrimage circuit", meta: "Multi-day", icon: RouteIcon },
  { title: "Do Dham Yatra", sub: "Twin-temple devotional journey", meta: "Multi-day", icon: RouteIcon },
  { title: "Golden Triangle Express", sub: "Delhi · Agra · Jaipur adventure", meta: "5 days", icon: CalendarDays },
];

export const Tours = () => {
  return (
    <section id="tours" data-testid="tours-section" className="relative bg-jodhpur-bg py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <RevealOnScroll>
          <span className="text-xs uppercase tracking-[0.3em] text-jodhpur-accent font-semibold">Tours & Experiences</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-jodhpur-primary tracking-tight max-w-2xl">
            Curated <span className="italic">journeys</span> with a driver-guide
          </h2>
        </RevealOnScroll>

        {/* One-day sightseeing */}
        <div className="mt-14">
          <p className="text-sm uppercase tracking-[0.2em] text-jodhpur-secondary font-semibold mb-6">One-day sightseeing tours</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ONE_DAY.map((t, i) => (
              <motion.a
                key={t.city}
                href={whatsappUrl(`Namaste! I'm interested in the One Day ${t.city} Sightseeing Tour.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`tour-${t.city}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-3xl bg-white border border-jodhpur-primary/10 p-6 hover:shadow-[0_24px_60px_-30px_rgba(20,41,89,0.5)] transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between">
                  <span className="h-12 w-12 rounded-2xl bg-jodhpur-primary/8 flex items-center justify-center text-jodhpur-primary">
                    <t.icon className="h-6 w-6" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-jodhpur-primary/40 transition-transform group-hover:rotate-45 group-hover:text-jodhpur-accent" />
                </div>
                <h3 className="mt-5 font-display text-2xl text-jodhpur-primary">One Day {t.city}</h3>
                <p className="mt-2 text-sm text-jodhpur-ink/60 leading-relaxed">{t.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Multi-day packages */}
        <div className="mt-16">
          <p className="text-sm uppercase tracking-[0.2em] text-jodhpur-secondary font-semibold mb-6">Rajasthan tour packages</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PACKAGES.map((p, i) => (
              <motion.a
                key={p.title}
                href={whatsappUrl(`Namaste! I'd like details on the "${p.title}" tour package.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`package-${p.title}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative overflow-hidden rounded-3xl bg-jodhpur-primary p-6 text-white hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-jodhpur-accent/20 blur-2xl" />
                <span className="relative h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center text-jodhpur-accent">
                  <p.icon className="h-6 w-6" />
                </span>
                <span className="relative mt-5 inline-block rounded-full bg-white/10 px-3 py-1 text-[11px] uppercase tracking-wider">{p.meta}</span>
                <h3 className="relative mt-3 font-display text-2xl leading-tight">{p.title}</h3>
                <p className="relative mt-2 text-sm text-white/70">{p.sub}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
